/* ══════════════════════════════════════════════════════════════
   Storage Service — Local storage manager with typed access
   Provides safe get/set/remove for localStorage with
   JSON serialization and quota-aware writes
   ══════════════════════════════════════════════════════════════ */

export interface StorageOptions {
    prefix?: string;
    ttl?: number;           // seconds (0 = no expiry)
}

interface StoredItem<T> {
    data: T;
    timestamp: number;
    ttl: number;
}

const DEFAULT_PREFIX = 'indieye_';

/** Check if localStorage is available */
function isAvailable(): boolean {
    try {
        const key = '__storage_test__';
        localStorage.setItem(key, '1');
        localStorage.removeItem(key);
        return true;
    } catch {
        return false;
    }
}

/** Get item from storage with TTL check */
export function getItem<T>(key: string, prefix: string = DEFAULT_PREFIX): T | null {
    if (!isAvailable()) return null;
    try {
        const raw = localStorage.getItem(prefix + key);
        if (!raw) return null;

        const stored: StoredItem<T> = JSON.parse(raw);

        // Check TTL
        if (stored.ttl > 0) {
            const age = (Date.now() - stored.timestamp) / 1000;
            if (age > stored.ttl) {
                localStorage.removeItem(prefix + key);
                return null;
            }
        }

        return stored.data;
    } catch {
        return null;
    }
}

/** Set item in storage with optional TTL */
export function setItem<T>(key: string, data: T, options: StorageOptions = {}): boolean {
    if (!isAvailable()) return false;
    const prefix = options.prefix || DEFAULT_PREFIX;

    try {
        const stored: StoredItem<T> = {
            data,
            timestamp: Date.now(),
            ttl: options.ttl || 0,
        };
        localStorage.setItem(prefix + key, JSON.stringify(stored));
        return true;
    } catch (err) {
        // Quota exceeded — try to clear old items
        if (err instanceof DOMException && err.name === 'QuotaExceededError') {
            clearExpired(prefix);
            try {
                const stored: StoredItem<T> = { data, timestamp: Date.now(), ttl: options.ttl || 0 };
                localStorage.setItem(prefix + key, JSON.stringify(stored));
                return true;
            } catch {
                return false;
            }
        }
        return false;
    }
}

/** Remove item from storage */
export function removeItem(key: string, prefix: string = DEFAULT_PREFIX): void {
    if (!isAvailable()) return;
    localStorage.removeItem(prefix + key);
}

/** Clear all items with the given prefix */
export function clearAll(prefix: string = DEFAULT_PREFIX): void {
    if (!isAvailable()) return;
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) keys.push(key);
    }
    for (const key of keys) localStorage.removeItem(key);
}

/** Clear all expired items */
export function clearExpired(prefix: string = DEFAULT_PREFIX): number {
    if (!isAvailable()) return 0;
    let cleared = 0;
    const now = Date.now();

    for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(prefix)) continue;

        try {
            const raw = localStorage.getItem(key);
            if (!raw) continue;
            const stored: StoredItem<unknown> = JSON.parse(raw);
            if (stored.ttl > 0 && (now - stored.timestamp) / 1000 > stored.ttl) {
                localStorage.removeItem(key);
                cleared++;
            }
        } catch {
            // Corrupted entry, remove it
            localStorage.removeItem(key);
            cleared++;
        }
    }

    return cleared;
}

/** Get storage usage stats */
export function getStorageStats(prefix: string = DEFAULT_PREFIX): {
    itemCount: number;
    totalBytes: number;
    expiredCount: number;
} {
    if (!isAvailable()) return { itemCount: 0, totalBytes: 0, expiredCount: 0 };

    let itemCount = 0, totalBytes = 0, expiredCount = 0;
    const now = Date.now();

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(prefix)) continue;
        itemCount++;
        const val = localStorage.getItem(key) || '';
        totalBytes += key.length + val.length;

        try {
            const stored: StoredItem<unknown> = JSON.parse(val);
            if (stored.ttl > 0 && (now - stored.timestamp) / 1000 > stored.ttl) expiredCount++;
        } catch { /* not a TTL entry */ }
    }

    return { itemCount, totalBytes, expiredCount };
}
